/**
 * Playwright PDF 印刷模式专用页面 — 班级报告 / 企业全员报告
 *
 * 路由：
 *   /print/class/:id?token=<hmac>           → scope="class"
 *   /print/enterprise-team/:id?token=<hmac> → scope="enterprise_team"
 *
 * 关键：
 *  - 不嵌 PrivateRoute（App.js 路由表里挂在公共段）。鉴权由后端 print-data
 *    API 的 HMAC token 校验承担。
 *  - id='preview' / 'demo' → 走本地 mock，无需 token（方便本地调样式）。
 *  - 数据 + 渲染完后，双 rAF + 300ms 延迟设置
 *    `document.documentElement.dataset.reportReady = '1'`，Playwright 用
 *    `page.wait_for_selector('html[data-report-ready="1"]')` 同步。
 */
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import ClassReport from '../components/report_class/ClassReport';
import { mockClassReportPayload } from '../mocks/classReportMock';
import '../styles/print.css';
import '../components/report_class/class-report.css';

// 走原生 fetch 而非 api/client.js: 后者拦截器会注 Authorization、401 跳
// /login、5xx 改写 detail —— 印刷模式只认 HMAC token，这些拦截器全是噪音。
async function fetchClassPrintData(id, token, scope) {
  let url;
  if (scope === 'enterprise_team') {
    url = `/api/dashboard/enterprises/${encodeURIComponent(id)}/print-data/?token=${encodeURIComponent(token)}`;
  } else {
    url = `/api/dashboard/classes/${encodeURIComponent(id)}/print-data/?token=${encodeURIComponent(token)}`;
  }

  const resp = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'omit', // 不带 cookie，强制走 token 路径
  });

  let body = null;
  try {
    body = await resp.json();
  } catch (_) {
    body = null;
  }
  return { ok: resp.ok, status: resp.status, body };
}

export default function PrintClassReportPage({ scope = 'class' }) {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const id = params.id || 'preview';
  const token = searchParams.get('token') || '';

  const [payload, setPayload] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  // Step 1: 拉数据
  useEffect(() => {
    let cancelled = false;

    async function load() {
      const isMock = id === 'preview' || id === 'demo';
      if (isMock) {
        // 模拟少量延迟，让组件拿到稳定 layout
        await new Promise((r) => setTimeout(r, 50));
        if (!cancelled) setPayload(mockClassReportPayload);
        return;
      }
      if (!token) {
        setErrorMsg(
          `缺少 token 参数。印刷模式 URL 形如 /print/${scope === 'enterprise_team' ? 'enterprise-team' : 'class'}/<id>?token=<hmac>。`
        );
        return;
      }
      const { ok, status, body } = await fetchClassPrintData(id, token, scope);
      if (cancelled) return;
      if (!ok) {
        const detail = body?.detail || `HTTP ${status}`;
        setErrorMsg(`拉 print-data 失败 (${status}): ${detail}`);
        return;
      }
      setPayload(body);
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [id, token, scope]);

  // Step 2: 数据渲染完后，置 ready 信号
  // 双 rAF + 300ms：第一个 rAF 等 React 渲染，第二个 rAF 等 ResizeObserver
  // 回调，再 300ms 让动画收敛 / 字体渲染稳定。
  useEffect(() => {
    if (!payload) return undefined;
    let raf1 = 0;
    let raf2 = 0;
    let timer = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        timer = window.setTimeout(() => {
          document.documentElement.dataset.reportReady = '1';
          window.__reportReady = true; // 旁路信号，调试用
        }, 300);
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      if (timer) window.clearTimeout(timer);
      delete document.documentElement.dataset.reportReady;
      window.__reportReady = false;
    };
  }, [payload]);

  if (errorMsg) {
    return (
      <div style={{ padding: 40, fontFamily: 'sans-serif' }}>
        <h2>印刷模式暂未就绪</h2>
        <p>{errorMsg}</p>
      </div>
    );
  }
  if (!payload) {
    return (
      <div style={{ padding: 40, fontFamily: 'sans-serif' }}>正在加载报告…</div>
    );
  }

  return <ClassReport payload={payload} printMode scope={scope} />;
}
